import {
  Button,
  Descriptions,
  Edit,
  EmptyState,
  FormCard,
  NotStartedIcon,
  PageHeader
} from '@lmc/ui-kit'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { ButtonsContainer, PageWrapper, SectionHeader } from '../../components'
import { getIsContractPreferencesEmpty, labelStyle } from '../../utils'
import { styled } from 'styled-components'
import AvailabilityPreferenceTable from './components/AvailabilityPreferenceTable'
import { useRouter } from 'hooks/useRouter'
import useUser from 'hooks/useUser/useUser'
import { HomeDto, User } from '@lmc/ts-common'
import dayjs from 'dayjs'
import { getContractItems, getOtherItems } from '../utils'
import { useSelector } from 'react-redux'
import {
  DATE_FORMAT,
  TIME_FORMAT,
  TIME_LAST_DATE_FORMAT
} from 'screens/Rostering/utils.datetime'
import useHomeType from 'hooks/useHomeType'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import { Link } from 'react-router-dom'
import { useMemo } from 'react'
import { OverrideLevel } from '../../hooks/usePaymentCards'
import { formatRateDisplay } from '../../siteSettings/components/PayRateCard'
import { useShiftTypesList } from '../../hooks/useShiftTypesList'
import { useVisitTypesList } from '../../hooks/useVisitTypesList'
import { useGetUserSettings } from '../../hooks/useUserSettings'

export const ContractAndPreferencesScreen = () => {
  const { t } = useTranslation()
  const { location, navigate, params } = useRouter()
  const { user } = useUser({ id: params?.id, includes: ['rostering'] })
  const userWithWorkProfile = user as unknown as User
  const { user: userUpdatedBy } = useUser({
    id: user?.workProfile?.updatedBy as unknown as string
  })
  const { hasRota } = useHomeType()

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  const flags = useFeatureFlags({ flagNames: ['payrates'] })?.data
  const isPayRatesEnabled = flags?.payrates?.enabled

  const { data: userSettings } = useGetUserSettings(
    isPayRatesEnabled ? params?.id : undefined
  )
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()

  const eventTypeNameMap = useMemo<Record<string, string>>(() => {
    const allEventTypes = [
      ...(shiftTypes || [])
        .filter((st) => st.id)
        .map((st) => ({ id: st.id!, name: st.name })),
      ...(visitTypes || [])
        .filter((vt) => vt.id)
        .map((vt) => ({ id: vt.id!, name: vt.name }))
    ]
    return Object.fromEntries(allEventTypes.map((et) => [et.id, et.name]))
  }, [shiftTypes, visitTypes])

  const assignedPayRateCard = userSettings?.payrate?.payRateCard

  const hasEmptyContractPreferencesEmpty = getIsContractPreferencesEmpty(user)

  const headerSubtitle = userUpdatedBy
    ? t('LAST_UPDATED_DATE_BY', {
        date: dayjs(user?.workProfile?.editedOn).format(TIME_LAST_DATE_FORMAT),
        by: userUpdatedBy?.name ? '- By' : '',
        user: `${userUpdatedBy?.name?.first} ${userUpdatedBy?.name?.last}`
      })
    : undefined

  return (
    <>
      <PageHeader
        title={hasRota ? t('CONTRACT_AND_PREFERENCES') : t('CONTRACT')}
        sticky
        subtitle={headerSubtitle}
        data-testid="contract-and-preferences-header"
      >
        <ButtonsContainer data-testid="contract-and-preferences-actions">
          <Button
            variant="primary"
            onClick={() => navigate(`${location.pathname}/edit`)}
            data-cy="edit-contract-btn"
            data-testid="contract-and-preferences-edit-button"
          >
            <Edit />
            {t('EDIT')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper data-testid="contract-and-preferences-content">
        {!hasEmptyContractPreferencesEmpty && (
          <>
            <SectionHeader
              title={t('INFORMATION')}
              data-testid="contract-and-preferences-information-section"
            />
            <CardsContainer data-testid="contract-and-preferences-cards-container">
              <InfoCard
                title={t('CONTRACT_INFORMATION')}
                data-testid="contract-and-preferences-contract-card"
              >
                <Descriptions
                  bordered
                  column={1}
                  labelStyle={labelStyle}
                  items={getContractItems(
                    userWithWorkProfile.workProfile,
                    t,
                    home
                  )}
                  data-testid="contract-and-preferences-contract-descriptions"
                />
              </InfoCard>
              {hasRota &&
                (isPayRatesEnabled ||
                  !!getOtherItems(userWithWorkProfile.workProfile, t)
                    ?.length) && (
                  <InfoCard
                    title={t('OTHER')}
                    data-testid="contract-and-preferences-other-card"
                  >
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={labelStyle}
                      items={[
                        ...(isPayRatesEnabled
                          ? [
                              {
                                key: 'payRate',
                                label: (
                                  <PayRateLabelWrapper>
                                    <Text>{t('PAY_RATE')}</Text>
                                    <Tooltip
                                      title={
                                        <TooltipText>
                                          {t('PAY_RATE_POPOVER_PREFIX')}{' '}
                                          <TooltipLink to="/settings/pay-rates">
                                            {t('PAY_RATE_POPOVER_LINK')}
                                          </TooltipLink>{' '}
                                          {t('PAY_RATE_POPOVER_SUFFIX')}
                                        </TooltipText>
                                      }
                                      overlayInnerStyle={{
                                        backgroundColor: '#000',
                                        borderRadius: '9px',
                                        padding: '8px 12px',
                                      }}
                                      color="#000"
                                    >
                                      <IconWrapper>
                                        <QuestionMarkIcon />
                                      </IconWrapper>
                                    </Tooltip>
                                  </PayRateLabelWrapper>
                                ),
                                children: assignedPayRateCard ? (
                                  <Text>
                                    <BoldText>{assignedPayRateCard.name}</BoldText>
                                    {userSettings?.payrate?.effectiveFrom && (
                                      <span>
                                        <br />
                                        {t('EFFECTIVE_FROM')}:{' '}
                                        {dayjs(userSettings.payrate.effectiveFrom).format(DATE_FORMAT)}{' '}
                                        {dayjs(userSettings.payrate.effectiveFrom).format(TIME_FORMAT)}
                                      </span>
                                    )}
                                    {assignedPayRateCard.rates.map((rate) => {
                                      const label =
                                        rate.level === OverrideLevel.BASE
                                          ? t('BASE_RATE')
                                          : eventTypeNameMap[rate.eventTypeId!] ||
                                            rate.eventTypeId
                                      return (
                                        <span key={rate.eventTypeId ?? 'base'}>
                                          <br />
                                          {label}: {formatRateDisplay(rate.rate, rate.rateType, t)}
                                        </span>
                                      )
                                    })}
                                  </Text>
                                ) : (
                                  <Text>-</Text>
                                )
                              }
                            ]
                          : []),
                        ...getOtherItems(userWithWorkProfile.workProfile, t)
                      ]}
                      data-testid="contract-and-preferences-other-descriptions"
                    />
                  </InfoCard>
                )}
            </CardsContainer>
            {hasRota && (
              <FormCard
                title={t('AVAILABILITY_PREFERENCE')}
                subtitle={t('AVAILABILITY_PREFERENCE_DESC')}
                data-testid="contract-and-preferences-availability-card"
              >
                <AvailabilityPreferenceTable
                  availability={user?.workProfile?.availability}
                  data-testid="contract-and-preferences-availability-table"
                />
              </FormCard>
            )}
          </>
        )}
        {hasEmptyContractPreferencesEmpty && (
          <EmptyState
            title={t('EMPTY_STATE_NOT_STARTED_TITLE')}
            icon={<NotStartedIcon />}
            description={t('EMPTY_STATE_NOT_STARTED_CONTRACT')}
            data-testid="contract-and-preferences-empty-state"
          />
        )}
      </PageWrapper>
    </>
  )
}

const CardsContainer = styled.div`
  display: flex;
  gap: 24px;
`

const InfoCard = styled(FormCard)`
  flex: 1;
  justify-content: flex-start;
`

const PayRateLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const IconWrapper = styled.span`
  display: flex;
  cursor: pointer;
`

const TooltipText = styled(Text)`
  color: #fff;
  font-weight: 400;
`

const BoldText = styled.span`
  font-weight: 600;
`

const TooltipLink = styled(Link)`
  color: #69b1ff;
  text-decoration: underline;

  &:hover {
    color: #91caff;
  }
`
